import { useBreakpoints } from 'hooks/useBreakpoints';
import styles from './ListGalleryModule.module.scss';

import throttle from 'lodash/throttle';
import { useEffect, useState } from 'react';

import { ListGalleryVariant } from 'api/contentful/contentTypes.cloverdotcom';

/**
 * A hook to check positioning to determine which text element is within canvas
 * @returns previous and current active index
 */
export function useListGalleryScroll(variant: ListGalleryVariant, rootEl: HTMLElement | null) {
  const [indexes, setIndexes] = useState({ activeItemIndex: 0, prevActiveItemIndex: 0 });
  const { browser } = useBreakpoints();
  const isDesktop = browser.is.desktop;

  useEffect(() => {
    if (variant !== ListGalleryVariant.STICKY_SCROLL) return;

    const update = throttle(updateActiveIndex, 500);

    window.addEventListener('resize', update);
    window.addEventListener('scroll', update);
    update();

    function updateActiveIndex() {
      if (!rootEl) return;
      const stickyEl = rootEl.querySelector(`.${styles.sticky}`);
      if (!stickyEl) return;
      const rect = stickyEl.getBoundingClientRect();
      const texts = rootEl.querySelectorAll(`.${styles.stickyTextItems} .${styles.item}`);
      const lastIndex = texts.length - 1;
      const index = Array.from(texts).findIndex((_, idx) => {
        const text = texts[lastIndex - idx];
        const { top, bottom } = text.getBoundingClientRect();
        return isDesktop ? top < rect.bottom : rect.bottom > bottom;
      });
      let activeItemIndex = lastIndex - index;
      if (!isDesktop && activeItemIndex < lastIndex) activeItemIndex += 1;
      if (!texts[activeItemIndex]) activeItemIndex = 0;
      setIndexes(prev => {
        if (activeItemIndex === prev.activeItemIndex) return prev;
        return { prevActiveItemIndex: prev.activeItemIndex, activeItemIndex };
      });
    }

    return () => {
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', update);
    };
  }, [variant, rootEl, isDesktop]);

  return indexes;
}
